<script setup lang="ts">
import EditIcon from '@/assets/icons/EditIcon.vue';
import TextInput from '@/core/elements/TextInput.vue';
import {
  CLASS_OR_GROUP_NAME_MAX_LENGTH,
  CLASS_OR_GROUP_NICKNAME_MAX_LENGTH,
} from 'common/constants/school.class.constants';
import { normalizeWhitespace } from 'common/utils/string.utils';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

const props = defineProps<{
  name: string;
  nickname: string;
  appearanceShowsName: boolean;
}>();
const emit = defineEmits<{
  commit: [value: { name: string; nickname: string }];
  'update:hasError': [value: boolean];
}>();

const isEditing = ref(false);
const name = ref(props.name);
const nickname = ref(props.nickname);
const trigger = useTemplateRef<HTMLButtonElement>('trigger');
const nicknameInput = useTemplateRef('nicknameInput');
const hasError = computed(() => isEditing.value && !name.value.trim());
watch(hasError, (value) => emit('update:hasError', value));

watch([() => props.name, () => props.nickname], () => {
  if (isEditing.value) return;
  name.value = props.name;
  nickname.value = props.nickname;
});

const startEditing = async (): Promise<void> => {
  name.value = props.name;
  nickname.value = props.nickname;
  isEditing.value = true;
  await nextTick();
  nicknameInput.value?.focus();
};

const finishEditing = (returnFocus = false): void => {
  if (!isEditing.value || hasError.value) return;
  name.value = normalizeWhitespace(name.value);
  nickname.value = normalizeWhitespace(nickname.value);
  isEditing.value = false;
  if (name.value !== props.name || nickname.value !== props.nickname) {
    emit('commit', { name: name.value, nickname: nickname.value });
  }
  if (returnFocus) void nextTick(() => trigger.value?.focus());
};

const cancel = (): void => {
  name.value = props.name;
  nickname.value = props.nickname;
  isEditing.value = false;
  void nextTick(() => trigger.value?.focus());
};

const handleFocusOut = (event: FocusEvent): void => {
  if (!(event.currentTarget instanceof HTMLElement)) return;
  if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
  // Commit both fields together when leaving the editor, not when moving between them.
  finishEditing();
};
</script>

<template>
  <div class="min-w-0">
    <button
      v-if="!isEditing"
      ref="trigger"
      type="button"
      class="flex max-w-full items-center gap-md rounded-sm text-left outline-inner"
      :aria-label="$t('general.edit')"
      @click="startEditing"
    >
      <span class="min-w-0 break-words text-title" :class="!nickname && 'text-secondary'">
        {{ nickname || $t('general.name') }}
      </span>
      <span v-if="!appearanceShowsName" class="shrink-0 text-title text-secondary">{{ name }}</span>
      <EditIcon class="size-icon-sm shrink-0 text-secondary" />
    </button>
    <div
      v-else
      class="flex min-w-0 flex-wrap items-start gap-md"
      @focusout="handleFocusOut"
      @keydown.enter.capture.prevent.stop="finishEditing(true)"
      @keydown.esc.capture.prevent.stop="cancel"
    >
      <TextInput
        ref="nicknameInput"
        v-model="nickname"
        class="h-14 min-w-0 flex-1 basis-40"
        :label="$t('general.name')"
        :maxlength="CLASS_OR_GROUP_NICKNAME_MAX_LENGTH"
      />
      <div class="w-26 shrink-0">
        <TextInput
          v-model="name"
          class="h-14"
          :label="$t('groups.abbreviation')"
          :maxlength="CLASS_OR_GROUP_NAME_MAX_LENGTH"
          :has-errors="hasError"
        />
        <p v-if="hasError" role="alert" class="mt-xs text-footer text-error">
          {{ $t('errorMessages.noAbbreviation') }}
        </p>
      </div>
    </div>
  </div>
</template>
